import React, { useState, useEffect, useCallback } from 'react';
import ddSvg from '../../assets/images/dd svg.svg';

interface DelightAnimationProps {
  type: 'success' | 'error';
  message?: string;
  onComplete: () => void;
  duration?: number; // in milliseconds
}

const DelightAnimation: React.FC<DelightAnimationProps> = ({
  type,
  message,
  onComplete,
  duration = 3000
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    // Wait for exit animation to complete before calling onComplete
    setTimeout(onComplete, 300);
  }, [onComplete]);

  // Add keyboard support for closing with Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isVisible, handleClose]);

  if (!isVisible) return null;

  return (
    <div 
      className={`delight-animation-overlay ${type}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: type === 'success' 
          ? 'rgba(0, 0, 0, 0.9)' 
          : 'rgba(0, 0, 0, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        animation: isVisible ? 'delightFadeIn 0.3s ease-out' : 'delightFadeOut 0.3s ease-in',
      }}
      onClick={(e) => {
        // Close when clicking on the overlay background
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      {/* Close Button */}
      <button
        onClick={handleClose}
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: 'rgba(255, 255, 255, 0.1)',
          border: '2px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          color: '#ffffff',
          fontSize: '24px',
          fontWeight: 'bold',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s ease',
          zIndex: 10001
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
          e.currentTarget.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
          e.currentTarget.style.transform = 'scale(1)';
        }}
        title="Close animation"
      >
        ✕
      </button>

      {/* Success Animation */}
      {type === 'success' && (
        <div 
          className="delight-success-container"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            animation: 'delightSuccessPulse 2s ease-in-out infinite'
          }}
        >
          {/* DD Logo with glow effect */}
          <div 
            style={{
              position: 'relative',
              marginBottom: 'var(--spacing-lg)',
              animation: 'delightLogoFloat 2s ease-in-out infinite'
            }}
          >
            <img
              src={ddSvg}
              alt="DareDevil Logo"
              style={{
                width: '200px',
                height: 'auto',
                filter: 'drop-shadow(0 0 20px #DB0004) drop-shadow(0 0 40px #DB0004)',
                animation: 'delightLogoGlow 2s ease-in-out infinite'
              }}
            />
            {/* Pulsing rings around logo */}
            <div 
              className="delight-ring"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '250px',
                height: '250px',
                border: '2px solid #DB0004',
                borderRadius: '50%',
                opacity: 0.6,
                animation: 'delightRingPulse 2s ease-in-out infinite'
              }}
            />
            <div 
              className="delight-ring"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '300px',
                height: '300px',
                border: '1px solid #DB0004',
                borderRadius: '50%',
                opacity: 0.3,
                animation: 'delightRingPulse 2s ease-in-out infinite 0.5s'
              }}
            />
          </div>

          {/* Success Message */}
          <div 
            style={{
              textAlign: 'center',
              color: '#ffffff',
              animation: 'delightTextSlideUp 0.6s ease-out 0.3s both'
            }}
          >
            <h2 
              style={{
                fontSize: '2.5rem',
                fontWeight: 'bold',
                margin: 0,
                marginBottom: 'var(--spacing-md)',
                background: 'linear-gradient(45deg, #DB0004, #FF6B35)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textShadow: '0 0 20px rgba(219, 0, 4, 0.5)'
              }}
            >
              BET CREATED!
            </h2>
            {message && (
              <p 
                style={{
                  fontSize: '1.2rem',
                  margin: 0,
                  color: '#ffffff',
                  opacity: 0.9
                }}
              >
                {message}
              </p>
            )}
          </div>

          {/* Confetti particles */}
          <div className="delight-confetti">
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                className="delight-confetti-particle"
                style={{
                  position: 'absolute',
                  width: '6px',
                  height: '6px',
                  background: ['#DB0004', '#FF6B35', '#FFD700', '#00FFFF'][i % 4],
                  left: `${Math.random() * 100}%`,
                  animation: `delightConfetti 3s ease-out ${Math.random() * 0.5}s both`
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Error Animation */}
      {type === 'error' && (
        <div 
          className="delight-error-container"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            animation: 'delightErrorShake 0.5s ease-in-out'
          }}
        >
          {/* Error Icon */}
          <div 
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'linear-gradient(45deg, #FF4444, #CC0000)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 'var(--spacing-lg)',
              boxShadow: '0 0 30px rgba(255, 68, 68, 0.6)',
              animation: 'delightErrorPulse 1s ease-in-out infinite'
            }}
          >
            <span 
              style={{
                fontSize: '4rem',
                color: '#ffffff',
                fontWeight: 'bold'
              }}
            >
              ✕
            </span>
          </div>

          {/* Error Message */}
          <div 
            style={{
              textAlign: 'center',
              color: '#ffffff',
              animation: 'delightTextSlideUp 0.6s ease-out 0.2s both'
            }}
          >
            <h2 
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                margin: 0,
                marginBottom: 'var(--spacing-md)',
                color: '#FF4444',
                textShadow: '0 0 20px rgba(255, 68, 68, 0.5)'
              }}
            >
              TRANSACTION FAILED
            </h2>
            {message && (
              <p 
                style={{
                  fontSize: '1.1rem',
                  margin: 0,
                  color: '#ffffff',
                  opacity: 0.9,
                  maxWidth: '400px',
                  lineHeight: '1.4'
                }}
              >
                {message}
              </p>
            )}
            <p 
              style={{
                fontSize: '0.9rem',
                margin: 'var(--spacing-md) 0 0 0',
                color: '#FFAAAA',
                opacity: 0.8
              }}
            >
              Please try again
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DelightAnimation;
